import type { SigningMode } from './api';

const OPTIONS: {
  value: SigningMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'client_side',
    label: 'Client-side (browser)',
    description:
      'Keys are generated and signing happens in the user\'s browser. Maximum user sovereignty — the key never leaves the browser. Includes recovery via email OTP.',
  },
  {
    value: 'tee',
    label: 'TEE (server-side)',
    description:
      'Keys are generated and signing happens in a hardware-attested Trusted Execution Environment. Enables agent/API signing. No recovery needed — keys are managed by the TEE.',
  },
];

export function SigningModeSelector({
  value,
  onChange,
}: {
  value: SigningMode;
  onChange: (v: SigningMode) => void;
}) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="block text-sm font-medium text-ink-primary">
        Signing mode
      </legend>
      <p className="text-xs text-ink-tertiary">
        This is a one-time choice that cannot be changed after creation.
      </p>
      <div className="mt-2 grid gap-3">
        {OPTIONS.map((opt) => {
          const selected = value === opt.value;
          return (
            <label
              key={opt.value}
              className={`relative flex cursor-pointer rounded-lg border px-4 py-3.5 transition-colors ${
                selected
                  ? 'border-bit2me bg-bit2me/5 ring-1 ring-bit2me'
                  : 'border-line bg-surface-white hover:border-ink-muted'
              }`}
            >
              <input
                type="radio"
                name="signing_mode"
                value={opt.value}
                checked={selected}
                onChange={() => onChange(opt.value)}
                className="sr-only"
                aria-describedby={`signing-mode-desc-${opt.value}`}
              />
              <div className="flex-1">
                <span className="block text-sm font-medium text-ink-primary">
                  {opt.label}
                </span>
                <span
                  id={`signing-mode-desc-${opt.value}`}
                  className="mt-1 block text-xs leading-relaxed text-ink-secondary"
                >
                  {opt.description}
                </span>
              </div>
              <div
                className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border ${
                  selected
                    ? 'border-bit2me bg-bit2me'
                    : 'border-ink-muted bg-surface-white'
                }`}
                aria-hidden="true"
              >
                {selected && (
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                )}
              </div>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
